import { Plus, Trash2 } from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';
import { SchedulerDsButton } from 'src/pages/operations/components/scheduler-ds-button';

const labelClassName = 'text-sm font-medium text-gray-700';

const textareaClassName = cn(
  'flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors',
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
  'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
);

export interface ObjectListEditorField<T extends Record<string, string>> {
  key: keyof T & string;
  label: string;
  placeholder?: string;
  multiline?: boolean;
}

export interface ObjectListEditorProps<T extends Record<string, string>> {
  label: string;
  values: T[];
  fields: ObjectListEditorField<T>[];
  onChange: (values: T[]) => void;
  createEmpty: () => T;
  addLabel?: string;
  emptyMessage?: string;
}

export function ObjectListEditor<T extends Record<string, string>>({
  label,
  values,
  fields,
  onChange,
  createEmpty,
  addLabel = 'Add row',
  emptyMessage = 'No rows yet.',
}: ObjectListEditorProps<T>) {
  const updateRow = (index: number, nextRow: T) => {
    onChange(values.map((value, itemIndex) => (itemIndex === index ? nextRow : value)));
  };

  const addRow = () => {
    onChange([...values, createEmpty()]);
  };

  const removeRow = (index: number) => {
    onChange(values.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">
          {label}
        </h3>
        <SchedulerDsButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={addRow}
        >
          <Plus className="size-4" />
          {addLabel}
        </SchedulerDsButton>
      </div>
      {values.length === 0 ? (
        <p className="text-sm text-gray-500">
          {emptyMessage}
        </p>
      ) : (
        <div className="space-y-3">
          {values.map((row, index) => (
            <div
              key={`${label}-${index}`}
              className="grid gap-3 rounded-md border border-gray-300 bg-gray-100/60 p-4 md:grid-cols-2"
            >
              {fields.map((field) => (
                <label
                  key={field.key}
                  className={cn('space-y-1', field.multiline && 'md:col-span-2')}
                >
                  <span className={labelClassName}>
                    {field.label}
                  </span>
                  {field.multiline ? (
                    <textarea
                      value={row[field.key]}
                      onChange={(event) => updateRow(index, { ...row, [field.key]: event.target.value })}
                      placeholder={field.placeholder}
                      rows={3}
                      className={textareaClassName}
                    />
                  ) : (
                    <Input
                      value={row[field.key]}
                      onChange={(event) => updateRow(index, { ...row, [field.key]: event.target.value })}
                      placeholder={field.placeholder}
                    />
                  )}
                </label>
              ))}
              <div className="md:col-span-2">
                <SchedulerDsButton
                  type="button"
                  variant="danger-ghost"
                  size="sm"
                  onClick={() => removeRow(index)}
                >
                  <Trash2 className="size-4" />
                  Remove row
                </SchedulerDsButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
